import React, { createContext, useContext, useState, useEffect } from "react"
import {
   createTimeLineHashmap,
   createOverridesHashmap,
   createdailyConfigurationHashmap,
   checkDateForOverride,
   getStageSequence,
   createStageHashmap,
} from "./ProductionSchedule.utils"

// Create the context
export const ProductionScheduleContext = createContext()

// Custom hook to use the production schedule context
export const useProductionScheduleContext = () => {
   const context = useContext(ProductionScheduleContext)
   if (!context) {
      throw new Error("Wrap in ProductionScheduleProvider")
   }

   const isOverrideDate = date => {
      return checkDateForOverride(date, context.overridesHashmap)
   }

   return {
      ...context,
      isOverrideDate,
   }
}

// Provider component
export const ProductionScheduleProvider = ({ children, value }) => {
   const [hashmaps, setHashmaps] = useState({
      timeLineHashMap: new Map(),
      overridesHashmap: new Map(),
      dailyConfigurationsMap: new Map(),
      stageSequenceIds: new Map(),
      stageHashMap: new Map(),
   })
   const [loading, setLoading] = useState(false)

   useEffect(() => {
      if (value.stages) {
         computeHashmaps()
      }
   }, [
      value.timeLineData,
      value.overrides,
      value.dailyConfigurations,
      value.stages,
      value.timeLineId,
   ])

   const computeHashmaps = async () => {
      if (
         !value.timeLineData &&
         !value.overrides &&
         !value.dailyConfigurations
      ) {
         setHashmaps({
            timeLineHashMap: new Map(),
            overridesHashmap: new Map(),
            dailyConfigurationsMap: new Map(),
         })
         setLoading(false)
         return
      }

      setLoading(true)
      try {
         const timeLineHashMap = createTimeLineHashmap(
            value.timeLineData,
            value.stages,
            value.timeLineId
         )
         const stageHashMap = createStageHashmap(value.stages)
         const overridesHashmap = createOverridesHashmap(value.overrides)
         const dailyConfigurationsMap = createdailyConfigurationHashmap(
            value.dailyConfigurations
         )
         const stageSequenceIds = getStageSequence(value.stages)

         setHashmaps({
            timeLineHashMap,
            overridesHashmap,
            dailyConfigurationsMap,
            stageSequenceIds,
            stageHashMap,
         })
      } catch (error) {
         console.error("Error computing hashmaps:", error)
      } finally {
         setLoading(false)
      }
   }

   const changeTimeLineData = timeLineDataHashmap => {
      if (!timeLineDataHashmap) return
      setHashmaps(prevHashmaps => ({
         ...prevHashmaps,
         timeLineHashMap: timeLineDataHashmap,
      }))
   }
   const contextValue = {
      ...hashmaps,
      changeTimeLineData,
      loading,
   }

   return (
      <ProductionScheduleContext.Provider value={contextValue}>
         {children}
      </ProductionScheduleContext.Provider>
   )
}
